import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import DayPicker, { DateUtils, DayModifiers, Modifier, RangeModifier } from 'react-day-picker';
import MomentLocaleUtils from 'react-day-picker/moment';
import { useLingui } from '@lingui/react';
import moment from 'moment';

import SwitchWithText from '@rover/components/src/buttons/OnOffSwitch/SwitchWithText';
import {
  DatePickerCaption,
  DatePickerNavbar,
  DatePickerStylingWrapper,
} from '@rover/components/src/datetime/DatePicker';
import DayOfWeekPicker from '@rover/components/src/datetime/DayOfWeekPicker';
import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import Calendar from '@rover/icons/svg/calendar.svg';
import { Box, Button, Flex, Text } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import dayFactory from '@rover/react-lib/src/factories/dayFactory';
import {
  RECURRING_SWITCH_INFO_CONTENT,
  RECURRING_SWITCH_INFO_TITLE,
  RECURRING_SWITCH_TEXT_LONG,
} from '@rover/react-lib/src/pages/contact-sitter/ContactSitterPage/components/ServiceScheduler/constants';
import { formatDateRange } from '@rover/react-lib/src/pages/search/SearchPage/components/StickyFiltersBar/utils';
import getMaxRequestDate from '@rover/react-lib/src/utils/getMaxRequestDate';
import type { Day } from '@rover/types/src/datetime/Day';

import { toggleLooseDate } from '../../utils/looseDates';
import { sortWeekDays } from '../../utils/recurringDates';

export type DateRange = {
  startDate?: Date;
  endDate?: Date;
};

export type DateSelection = DateRange & {
  dates?: Date[];
  // A repeating selection replaces the range with a start date and the weekdays it repeats on.
  isRecurring?: boolean;
  weekDays?: number[];
};

type Props = DateSelection & {
  isOpen: boolean;
  onClose: () => void;
  onSave: (selection: DateSelection) => void;
  locale: Locale;
  monthsShown: number;
  isMobileBrowser?: boolean;
  // Daytime services are booked day by day, so their days need not be contiguous.
  allowsLooseDates?: boolean;
  // Services billed weekly can repeat, so they offer the one-time / repeat switch.
  allowsRecurring?: boolean;
  testID: string;
};

// Clicking outside of an in-progress range restarts it, matching the range
// pickers used by the search filters.
export const addDayToRange = (day: Date, { startDate, endDate }: DateRange): DateRange => {
  if (!startDate || DateUtils.isDayBefore(day, startDate) || (startDate && endDate)) {
    return { startDate: day, endDate: undefined };
  }
  if (DateUtils.isSameDay(day, startDate)) return { startDate, endDate: undefined };
  return { startDate, endDate: day };
};

const RevampDatesPicker = ({
  isOpen,
  onClose,
  onSave,
  locale,
  startDate,
  endDate,
  dates,
  isRecurring,
  weekDays,
  monthsShown,
  isMobileBrowser,
  allowsLooseDates,
  allowsRecurring,
  testID,
}: Props): JSX.Element => {
  const { i18n } = useLingui();
  const theme = useTheme();
  const weekDaysLabelId = useId();
  const [range, setRange] = useState<DateRange>({ startDate, endDate });
  const [looseDates, setLooseDates] = useState<Date[]>(dates ?? []);
  const [repeats, setRepeats] = useState<boolean>(isRecurring === true);
  const [selectedWeekDays, setSelectedWeekDays] = useState<number[]>(weekDays ?? []);

  // Reopening the picker always starts from the dates currently applied to the page.
  useEffect(() => {
    if (!isOpen) return;
    setRange({ startDate, endDate });
    setLooseDates(dates ?? []);
    setRepeats(isRecurring === true);
    setSelectedWeekDays(weekDays ?? []);
  }, [isOpen, startDate, endDate, dates, isRecurring, weekDays]);

  // Only a service that repeats can be in repeat mode, so switching to one that does
  // not — the panel stays mounted while the service changes — falls back to one time.
  const isRepeating = repeats && allowsRecurring === true;

  const handleDayClick = useCallback(
    (day: Date, modifiers: DayModifiers) => {
      if (modifiers.disabled) return;
      // A repetition is described by its first day, so the calendar picks a start date.
      if (isRepeating) {
        setRange({ startDate: day, endDate: undefined });
        return;
      }
      if (allowsLooseDates) {
        setLooseDates((current) => toggleLooseDate(day, current));
        return;
      }
      setRange((current) => addDayToRange(day, current));
    },
    [allowsLooseDates, isRepeating]
  );

  const handleWeekDaysChange = useCallback((days: Day[]) => {
    setSelectedWeekDays(sortWeekDays(days.map((day) => Number(day.id))));
  }, []);

  // The day of week picker works with `Day`s keyed by their day-of-week index, and the
  // repetition needs no concrete date for them: the start date is what dates it.
  const selectedDayOfWeekOptions = useMemo(
    () => selectedWeekDays.map((weekDay) => dayFactory({ id: weekDay })),
    [selectedWeekDays]
  );

  const handleSave = useCallback(() => {
    if (isRepeating) {
      onSave({ startDate: range.startDate, isRecurring: true, weekDays: selectedWeekDays });
    } else {
      onSave(allowsLooseDates ? { dates: looseDates } : range);
    }
    onClose();
  }, [onSave, onClose, isRepeating, allowsLooseDates, looseDates, range, selectedWeekDays]);

  const today = moment().startOf('day').toDate();
  const maxDate = getMaxRequestDate();

  const selectedRange: RangeModifier | undefined =
    range.startDate && range.endDate ? { from: range.startDate, to: range.endDate } : undefined;

  // The days in between are styled apart from the endpoints, so they exclude them.
  const daysBetween: RangeModifier | undefined =
    range.startDate && range.endDate
      ? {
          from: moment(range.startDate).add(1, 'day').toDate(),
          to: moment(range.endDate).subtract(1, 'day').toDate(),
        }
      : undefined;

  // A range only needs its start date to be saved, which is what ends the stay that day.
  const canSave = ((): boolean => {
    if (isRepeating) return Boolean(range.startDate) && selectedWeekDays.length > 0;
    if (allowsLooseDates) return looseDates.length > 0;
    return Boolean(range.startDate);
  })();

  return (
    <Flex flexDirection="column" data-testid={testID}>
      {allowsRecurring && (
        <Box mb="2x" data-testid={`${testID}-frequency`}>
          <SwitchWithText
            textContent={i18n._(RECURRING_SWITCH_TEXT_LONG)}
            extraInfoContent={i18n._(RECURRING_SWITCH_INFO_CONTENT)}
            extraInfoTitle={i18n._(RECURRING_SWITCH_INFO_TITLE)}
            on={isRepeating}
            onChange={setRepeats}
            bigLabel={!isMobileBrowser}
          />
        </Box>
      )}

      {isRepeating && (
        <Box mb="2x">
          <Text
            size="200"
            fontWeight="semibold"
            mb="1x"
            webProps={{ as: 'label', id: weekDaysLabelId }}
          >
            {/* TODO: Pending translations. Address in DEV-157125 */}
            What days?
          </Text>
          <DayOfWeekPicker
            aria-labelledby={weekDaysLabelId}
            locale={locale}
            selectedDays={selectedDayOfWeekOptions}
            onChange={handleWeekDaysChange}
            bigLabel={!isMobileBrowser}
          />
        </Box>
      )}

      {isRepeating && (
        <Box mb="2x">
          <Text size="200" fontWeight="semibold" mb="1x">
            {/* TODO: Pending translations. Address in DEV-157125 */}
            When would you like to start?
          </Text>
          <Flex
            alignItems="center"
            justifyContent="space-between"
            gap="2x"
            px="4x"
            py="3x"
            borderRadius="8px"
            border={`1px solid ${theme.colors.separator.primary}`}
            data-testid={`${testID}-start-date`}
          >
            <Text size="200" textColor={range.startDate ? 'primary' : 'secondary'}>
              {range.startDate
                ? formatDateRange(range.startDate, undefined, locale)
                : // TODO: Pending translations. Address in DEV-157125
                  'Select your start date'}
            </Text>
            <Calendar
              width="20"
              height="20"
              fill={theme.colors.text.secondary.toString()}
              aria-hidden="true"
            />
          </Flex>
        </Box>
      )}

      <Box maxHeight="55vh" sx={{ overflowY: 'auto' }}>
        <DatePickerStylingWrapper newCalendarInteraction isMobileBrowser={isMobileBrowser}>
          <DayPicker
            numberOfMonths={monthsShown}
            initialMonth={
              (allowsLooseDates && !isRepeating ? looseDates[0] : range.startDate) ?? today
            }
            fromMonth={today}
            toMonth={maxDate}
            locale={locale}
            localeUtils={MomentLocaleUtils}
            navbarElement={DatePickerNavbar}
            captionElement={DatePickerCaption}
            selectedDays={(() => {
              if (isRepeating) return [range.startDate].filter(Boolean) as Modifier[];
              if (allowsLooseDates) return looseDates;
              return [range.startDate, selectedRange].filter(Boolean) as Modifier[];
            })()}
            disabledDays={[{ before: today, after: maxDate }]}
            // A repeating start date and the loose days are standalone selections, so
            // they take none of the modifiers that draw the bar connecting a range.
            modifiers={
              isRepeating || allowsLooseDates
                ? {}
                : {
                    start: range.startDate ?? [],
                    end: range.endDate ?? [],
                    between: daysBetween ?? [],
                  }
            }
            onDayClick={handleDayClick}
          />
        </DatePickerStylingWrapper>
      </Box>

      <Button mt="2x" variant="primary" fullWidth disabled={!canSave} onPress={handleSave}>
        {/* TODO: Pending translations. Address in DEV-157125 */}
        Save dates
      </Button>
    </Flex>
  );
};

export default RevampDatesPicker;
